import React, { useState } from "react";
import { Modal, Checkbox, Button, Space, Typography, Divider } from "antd";
import { ExportOutlined, SettingOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;

export interface ExportDetailOptions {
  includeRates: boolean;
  includeQuantities: boolean;
  includeDescriptions: boolean;
  includeNotes: boolean;
  includeTotals: boolean;
  includeOhp: boolean;
  includePreliminaries: boolean;
  includeBreakdown: boolean;
  groupByCategory: boolean;
}

interface ExportOptionsModalProps {
  visible: boolean;
  onClose: () => void;
  onExport: (
    options: ExportDetailOptions,
    format: "excel" | "pdf",
  ) => void | Promise<void>;
  title?: string;
}

const ExportOptionsModal: React.FC<ExportOptionsModalProps> = ({
  visible,
  onClose,
  onExport,
  title = "Export Options",
}) => {
  const [options, setOptions] = useState<ExportDetailOptions>({
    includeRates: true,
    includeQuantities: true,
    includeDescriptions: true,
    includeNotes: false,
    includeTotals: true,
    includeOhp: true,
    includePreliminaries: true,
    includeBreakdown: true,
    groupByCategory: true,
  });

  const handleOptionChange = (
    key: keyof ExportDetailOptions,
    checked: boolean
  ) => {
    setOptions((prev) => ({
      ...prev,
      [key]: checked,
    }));
  };

  // Tracks the export in flight so the button shows a spinner (and every
  // other control is disabled) until `onExport` — which awaits the full
  // Excel/PDF generation and browser download all the way down to
  // `exportGroundworksBillSummariesToExcel` — actually resolves, instead of
  // closing the modal the instant the click handler returns.
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async (format: "excel" | "pdf") => {
    setIsExporting(true);
    try {
      await onExport(options, format);
    } finally {
      setIsExporting(false);
      onClose();
    }
  };

  const selectAll = () => {
    setOptions({
      includeRates: true,
      includeQuantities: true,
      includeDescriptions: true,
      includeNotes: true,
      includeTotals: true,
      includeOhp: true,
      includePreliminaries: true,
      includeBreakdown: true,
      groupByCategory: true,
    });
  };

  const selectMinimal = () => {
    setOptions({
      includeRates: false,
      includeQuantities: true,
      includeDescriptions: true,
      includeNotes: false,
      includeTotals: true,
      includeOhp: false,
      includePreliminaries: false,
      includeBreakdown: false,
      groupByCategory: true,
    });
  };

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={isExporting ? undefined : onClose}
      width={600}
      centered
      closable={!isExporting}
      maskClosable={!isExporting}
      styles={{
        header: {
          backgroundColor: '#f5f5f5',
          marginLeft: '-24px',
          marginRight: '-24px',
          marginTop: '-20px',
          paddingLeft: '24px',
          paddingRight: '24px',
          paddingTop: '16px',
          paddingBottom: '16px'
        }
      }}
      footer={
        <div className="flex justify-end gap-2" style={{ borderTop: '1px solid #f0f0f0', paddingTop: '16px', marginLeft: '-24px', marginRight: '-24px', paddingLeft: '24px', paddingRight: '24px', marginBottom: '-20px', paddingBottom: '20px' }}>
          <Button onClick={onClose} disabled={isExporting}>
            Cancel
          </Button>
          <Button
            type="primary"
            icon={<ExportOutlined />}
            onClick={() => handleExport("excel")}
            loading={isExporting}
            style={{
              backgroundColor: '#1890ff',
              borderColor: '#1890ff'
            }}
          >
            {isExporting ? "Exporting…" : "Export to Excel"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <Title level={5}>Data Content</Title>
          <div className="grid grid-cols-2 gap-4">
            <Checkbox
              checked={options.includeDescriptions}
              onChange={(e) =>
                handleOptionChange("includeDescriptions", e.target.checked)
              }
            >
              <Text> Descriptions</Text>
            </Checkbox>
            <Checkbox
              checked={options.includeQuantities}
              onChange={(e) =>
                handleOptionChange("includeQuantities", e.target.checked)
              }
            >
              <Text>Quantities</Text>
            </Checkbox>
            <Checkbox
              checked={options.includeRates}
              onChange={(e) =>
                handleOptionChange("includeRates", e.target.checked)
              }
            >
              <Text>Rates</Text>
            </Checkbox>
            <Checkbox
              checked={options.includeTotals}
              onChange={(e) =>
                handleOptionChange("includeTotals", e.target.checked)
              }
            >
              <Text>Totals</Text>
            </Checkbox>
            {/* <Checkbox
              checked={options.includeOhp}
              onChange={(e) =>
                handleOptionChange("includeOhp", e.target.checked)
              }
            >
              <Text>OH&P Values</Text>
            </Checkbox>
            <Checkbox
              checked={options.includePreliminaries}
              onChange={(e) =>
                handleOptionChange("includePreliminaries", e.target.checked)
              }
            >
              <Text>Preliminaries</Text>
            </Checkbox> */}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ExportOptionsModal;
